import { Link } from "react-router-dom";
import { Home as HomeIcon, Building2, Factory, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Motion";
import { Ribbon } from "@/components/Ribbon";
import { IMAGES } from "@/lib/site";

const AUDIENCES = [
  {
    icon: HomeIcon,
    name: "Residential",
    tier: "Homeowner",
    image: IMAGES.residential,
    body: "Everyday people planning upgrades, remodels or fixes. Get clear guidance on panels, EV chargers, solar readiness, smart home, lighting and safety before you commit.",
    points: ["Panel & load assessments", "EV charger & solar planning", "Lighting & smart home design", "Code & safety reviews"],
  },
  {
    icon: Building2,
    name: "Commercial",
    tier: "Commercial",
    image: IMAGES.hero,
    body: "Offices, restaurants, retail and mixed-use spaces. Advice that keeps your business inspection-ready and downtime-free — tenant improvement strategy, equipment load planning and contingency planning.",
    points: ["Tenant improvement strategy", "Equipment & sub-panel load planning", "Permitting & compliance", "Downtime & phasing plans"],
  },
  {
    icon: Factory,
    name: "Industrial",
    tier: "Industrial",
    image: IMAGES.industrial,
    body: "Warehouses, production and heavy-load facilities. Three-phase distribution, controls, reliability and utility coordination — advised with 40+ years of precision.",
    points: ["Three-phase & distribution planning", "Thermal scanning & hot-spot detection", "Controls & automation review", "Preventative maintenance", "Utility / SCE coordination"],
  },
];

export default function Services() {
  return (
    <div>
      <PageHeader
        label="Services"
        titleLines={["Built For", "Every Job"]}
        sub="Advice organized by who you are. Whatever the scale, the goal is the same — the right plan, the first time."
      />

      <section className="mx-auto max-w-[1400px] px-5 pb-16 md:px-10">
        <div className="flex flex-col gap-8">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <Reveal key={a.name} delay={i * 0.05}>
                <div className="group grid gap-0 overflow-hidden rounded-2xl border border-white/10 bg-ink-700 transition-colors duration-300 hover:border-amber/40 md:grid-cols-2">
                  <div className={`relative min-h-[280px] overflow-hidden ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <img src={a.image} alt={a.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-ink-900/40" />
                    <span className="absolute left-6 top-6 flex h-12 w-12 items-center justify-center rounded-xl bg-amber text-black">
                      <Icon className="h-6 w-6" />
                    </span>
                  </div>
                  <div className="flex flex-col justify-center p-8 md:p-12">
                    <div className="flex items-center justify-between">
                      <h2 className="font-head text-4xl font-medium tracking-tight text-white md:text-5xl">{a.name}</h2>
                      <span className="text-xs font-semibold uppercase tracking-widest text-amber">Project-based fee</span>
                    </div>
                    <p className="mt-4 leading-relaxed text-white/60">{a.body}</p>
                    <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2">
                      {a.points.map((p) => (
                        <li key={p} className="flex items-center gap-2 text-sm text-white/70">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber" />{p}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      state={{ tier: a.tier }}
                      className="mt-8 inline-flex w-fit items-center gap-2 text-sm font-medium uppercase tracking-[0.2em] text-amber transition-all hover:gap-3"
                    >
                      Book {a.name} consult <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-amber/40 bg-ink-700 p-10 md:flex-row md:items-center">
            <div>
              <h3 className="font-head text-3xl font-medium tracking-tight text-white">Not sure which fits?</h3>
              <p className="mt-2 text-white/60">Call Manny directly and he'll point you the right way — no charge to ask.</p>
            </div>
            <Link to="/pricing" className="inline-flex items-center gap-3 rounded-full bg-amber px-7 py-4 font-medium uppercase tracking-[0.2em] text-black transition-transform duration-300 hover:scale-[1.04]">
              Compare Tiers
            </Link>
          </div>
        </Reveal>
      </div>

      <Ribbon direction="right" />
    </div>
  );
}
